import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { PlayCircle, Calendar, User, Mic, Share2, Check } from "lucide-react";
import AddToCollectionButton from "@/components/AddToCollectionButton";
import SaveToCaderno from "@/components/SaveToCaderno";
import { toast } from "sonner";
import { episodePath } from "@/lib/episodeUrl";
import { safeThumb } from "@/lib/youtubeThumb";
import { trackEvent } from "@/lib/ga4";

function slugify(text) {
  return (text || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w\s-]/g, "").trim().toLowerCase().replace(/[\s_-]+/g, "-");
}

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function supportsHover() {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

export default function EpisodeCard({ episode, variant = "default", featured = false }) {
  const nav = useNavigate();
  const thumb = safeThumb(episode.thumbnail, episode.youtube_id, {
    quality: featured ? "featured" : "list",
  });
  const size = variant === "shelf" ? "w-[280px] md:w-[320px] flex-none snap-start" : "w-full";
  const duration = formatDuration(episode.duration_seconds);
  const [preview, setPreview] = useState(false);
  const hoverTimer = useRef(null);

  useEffect(() => () => { if (hoverTimer.current) clearTimeout(hoverTimer.current); }, []);

  const startPreview = () => {
    if (!supportsHover() || !episode.youtube_id) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setPreview(true), 600);
  };
  const stopPreview = () => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
    setPreview(false);
  };

  const goToEpisode = () => nav(episodePath(episode));

  const [copied, setCopied] = useState(false);
  const copyShareLink = async (e) => {
    e.stopPropagation();
    const origin = typeof window !== "undefined" ? window.location.origin : "https://juristube.com.br";
    const shareUrl = `${origin}/api/og/episode/${episode.id}`;
    try {
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent || "")) {
        await navigator.share({ title: episode.title, url: shareUrl });
        trackEvent("share_click", {
          surface: "episode_card",
          method: "navigator_share",
          episode_id: episode.id,
          episode_title: episode.title,
          podcast_slug: episode.podcast_slug,
        });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copiado! Cole no WhatsApp, LinkedIn ou onde quiser — aparecerá com preview.", { duration: 4500 });
      trackEvent("share_click", {
        surface: "episode_card",
        method: "clipboard",
        episode_id: episode.id,
        episode_title: episode.title,
        podcast_slug: episode.podcast_slug,
      });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Não foi possível copiar o link.");
    }
  };

  const authors = episode.authors && episode.authors.length > 0
    ? episode.authors
    : (episode.author ? [episode.author] : []);

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goToEpisode}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToEpisode()}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
      onFocus={startPreview}
      onBlur={stopPreview}
      data-testid={`episode-card-${episode.id}`}
      className={`card-ep group block cursor-pointer ${size}`}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={thumb}
          alt={episode.title || "Episódio JurisTube"}
          width="320"
          height="180"
          className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${preview ? "opacity-0" : "opacity-100"}`}
          loading={featured ? "eager" : "lazy"}
          fetchPriority={featured ? "high" : "auto"}
          decoding="async"
          onError={(e) => {
            // Defensive fallback: when the locally-optimized WebP fails
            // (e.g. backend cold-start), or when YouTube hasn't propagated
            // ``maxresdefault.jpg`` yet, drop back to the universally-
            // available ``hqdefault.jpg`` so users never see a broken icon.
            const fallback = `https://i.ytimg.com/vi/${episode.youtube_id}/hqdefault.jpg`;
            if (e.currentTarget.src !== fallback) e.currentTarget.src = fallback;
          }}
        />
        {preview && (
          <iframe
            title={`preview-${episode.id}`}
            src={`https://www.youtube.com/embed/${episode.youtube_id}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&playsinline=1&disablekb=1&iv_load_policy=3&fs=0`}
            className="absolute inset-0 w-full h-full pointer-events-none"
            frameBorder="0"
            allow="autoplay; encrypted-media"
            data-testid={`card-preview-${episode.id}`}
          />
        )}
        <div className={`absolute inset-0 bg-gradient-to-t from-slate-950/60 via-slate-950/10 to-transparent pointer-events-none ${preview ? "opacity-30" : "md:opacity-80 opacity-40"} transition-opacity duration-300`} />
        {!preview && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
            <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center pulse-glow">
              <PlayCircle className="w-8 h-8 text-white" fill="currentColor" />
            </div>
          </div>
        )}
        {duration && (
          <span
            className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white bg-slate-950/85 backdrop-blur-sm tabular-nums opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
            data-testid={`card-duration-${episode.id}`}
          >
            {duration}
          </span>
        )}
        <AddToCollectionButton episode={episode} variant="compact" />
        <button
          type="button"
          onClick={copyShareLink}
          aria-label="Copiar link de compartilhamento com preview rico"
          title="Compartilhar (link com preview)"
          className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg transition-all z-10 ${
            copied
              ? "bg-emerald-600 text-white"
              : "bg-slate-950/70 text-slate-200 opacity-0 group-hover:opacity-100 hover:bg-red-600 hover:text-white"
          }`}
          data-testid={`card-share-${episode.id}`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
        </button>
      </div>
      <div className="p-4">
        <h3 className="font-serif text-base md:text-lg font-semibold text-white leading-snug line-clamp-2 group-hover:text-red-400 transition">
          {episode.title}
        </h3>
        <div className="mt-3 flex items-center gap-3 text-xs text-slate-400 flex-wrap">
          {authors.length > 0 && (
            <div className="inline-flex items-center gap-1.5 flex-wrap">
              <User className="w-3 h-3 shrink-0" />
              {authors.slice(0, 2).map((a, i) => (
                <Link
                  key={a}
                  to={`/autor/${slugify(a)}`}
                  onClick={(e) => e.stopPropagation()}
                  className="hover:text-sky-400 transition truncate max-w-[130px]"
                  data-testid={`card-author-link-${episode.id}-${i}`}
                >
                  {a}{i < Math.min(authors.length, 2) - 1 ? "," : ""}
                </Link>
              ))}
              {authors.length > 2 && <span className="text-slate-500">+{authors.length - 2}</span>}
            </div>
          )}
          {episode.year && (
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3 h-3" /> {episode.year}
            </span>
          )}
          {episode.podcast_name && episode.podcast_slug && (
            <Link
              to={`/podcast/${episode.podcast_slug}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-red-400 hover:text-red-300 transition truncate max-w-[180px]"
              data-testid={`card-podcast-link-${episode.id}`}
              title={episode.podcast_name}
            >
              <Mic className="w-3 h-3 shrink-0" />
              <span className="truncate">{episode.podcast_name}</span>
            </Link>
          )}
        </div>
        {episode.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {episode.tags.slice(0, 2).map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-wider text-sky-400/80">#{t}</span>
            ))}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-slate-800/60 flex justify-end" onClick={(e) => e.stopPropagation()}>
          <SaveToCaderno
            variant="compact"
            target={{
              type: "video",
              id: episode.id,
              meta: {
                title: episode.title,
                youtube_id: episode.youtube_id,
                podcast_slug: episode.podcast_slug,
                podcast_name: episode.podcast_name,
                authors,
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}
